// Sample Character Slice
// Redux state management for Voice Sample Characters
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Realigned to REACT_APP_API_URL_RAW (canonical name) in chore/deploy-prep.
const API_BASE_URL = process.env.REACT_APP_API_URL_RAW || 'http://localhost:5000';

// Async thunks for API calls
export const fetchPrimaryPortfolio = createAsyncThunk(
  'sampleCharacter/fetchPrimaryPortfolio',
  async (actorId) => {
    console.log('fetchPrimaryPortfolio actorId', actorId, 'url', `${API_BASE_URL}/api/actors/${actorId}/primary-portfolio`)
    const response = await axios.get(
      `${API_BASE_URL}/api/actors/${actorId}/primary-portfolio`
    );
    return response.data;
  }
);

export const fetchSampleCharacters = createAsyncThunk(
  'sampleCharacter/fetchSampleCharacters',
  async (portfolioId) => {
    const response = await axios.get(
      `${API_BASE_URL}/api/portfolios/${portfolioId}/sample-characters`
    );
    return response.data;
  }
);

export const createSampleCharacter = createAsyncThunk(
  'sampleCharacter/createSampleCharacter',
  async ({ portfolioId, data }) => {
    const response = await axios.post(
      `${API_BASE_URL}/api/portfolios/${portfolioId}/sample-characters`,
      { data }
    );
    return response.data;
  }
);

export const updateSampleCharacter = createAsyncThunk(
  'sampleCharacter/updateSampleCharacter',
  async ({ portfolioId, characterId, data }) => {
    const response = await axios.put(
      `${API_BASE_URL}/api/portfolios/${portfolioId}/sample-characters/${characterId}`,
      { data }
    );
    return response.data;
  }
);

export const deleteSampleCharacter = createAsyncThunk(
  'sampleCharacter/deleteSampleCharacter',
  async ({ characterId, cascadeDelete = false }) => {
    await axios.delete(
      `${API_BASE_URL}/api/sample-characters/${characterId}?cascadeDelete=${cascadeDelete}`
    );
    return characterId;
  }
);

export const getSampleCharacter = createAsyncThunk(
  'sampleCharacter/getSampleCharacter',
  async (characterId) => {
    const response = await axios.get(
      `${API_BASE_URL}/api/sample-characters/${characterId}`
    );
    return response.data;
  }
);

// Create sample character with complete data in one transaction
export const createSampleCharacterComplete = createAsyncThunk(
  'sampleCharacter/createComplete',
  async ({ portfolioId, characterData, mediaData }) => {
    const response = await axios.post(
      `${API_BASE_URL}/api/portfolios/${portfolioId}/sample-characters-complete`,
      { characterData, mediaData }
    );
    return response.data; // Complete character with defaultSpeechSample
  }
);

// Update sample character with complete data in one transaction
export const updateSampleCharacterComplete = createAsyncThunk(
  'sampleCharacter/updateComplete',
  async ({ portfolioId, characterId, characterData, mediaData }) => {
    try {
      console.log('[REDUX ACTION] updateSampleCharacterComplete called with:', {
        portfolioId,
        characterId,
        hasCharacterData: !!characterData,
        hasMediaData: !!mediaData
      });

      const url = `${API_BASE_URL}/api/portfolios/${portfolioId}/sample-characters-complete/${characterId}`;
      console.log('[REDUX ACTION] Making PUT request to:', url);

      const response = await axios.put(url, { characterData, mediaData });

      console.log('[REDUX ACTION] Response received:', response.data);
      return response.data; // Complete updated character with defaultSpeechSample
    } catch (error) {
      console.error('[REDUX ACTION ERROR] updateSampleCharacterComplete failed:', error);
      console.error('[REDUX ACTION ERROR] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }
);

// Create variation for a sample character
export const createVariationForSample = createAsyncThunk(
  'sampleCharacter/createVariation',
  async ({ characterId, data }) => {
    const response = await axios.post(
      `${API_BASE_URL}/api/characters/${characterId}/variations`,
      { data }
    );
    return { characterId, variation: response.data };
  }
);

// Update variation for a sample character
export const updateVariationForSample = createAsyncThunk(
  'sampleCharacter/updateVariation',
  async ({ variationId, data }) => {
    const response = await axios.put(
      `${API_BASE_URL}/api/variations/${variationId}`,
      data
    );
    return response.data;
  }
);

const initialState = {
  // Portfolio data
  primaryPortfolio: null,

  // Sample characters - Single source of truth
  sampleCharacters: [],
  selectedCharacterId: null,  // Store only the ID, not the object

  // UI state
  isCreatingNew: false,

  // Loading states
  loading: {
    portfolio: false,
    characters: false,
    create: false,
    update: false,
    delete: false,
    character: false
  },

  // Error states
  errors: {
    portfolio: null,
    characters: null,
    create: null,
    update: null,
    delete: null,
    character: null
  },

  // Update tracking system
  updateTracking: {
    pendingUpdates: {},      // { characterId: { field: value, ... } }
    updateQueue: [],         // Array of pending update operations
    lastUpdateTimestamps: {}, // { characterId: timestamp }
    optimisticUpdates: {},   // { characterId: optimistic data }
    updateStatus: {}         // { characterId: 'pending' | 'processing' | 'completed' | 'failed' }
  },

  // Form session state - temporary data during editing
  formSession: {
    // Original values (snapshot when form loads)
    original: null,

    // Current form values
    current: null,

    // Audio recording/upload state
    audio: {
      isRecording: false,
      recordingDuration: 0,
      audioBlob: null,        // The actual blob object
      audioUrl: null,         // blob: URL for playback
      audioSource: null,      // 'recording' | 'upload' | 'existing'
      uploadedFileName: '',
      uploadedFileSize: 0,
      isReplacing: false,
      error: null
    },

    // Avatar state
    avatar: {
      file: null,             // The actual file object
      previewUrl: null,       // blob: URL for preview
      hasChanges: false,
      uploadStatus: 'idle',   // 'idle' | 'uploading' | 'success' | 'error'
      uploadedUrl: null,
      error: null
    },

    // Change tracking
    changes: {
      hasCharacterChanges: false,
      hasAudioChange: false,
      hasTextChange: false,
      hasAvatarChange: false,
      isDirty: false,
      changedFields: []
    },

    // Form metadata
    lastSaved: null,
    saveError: null
  }
};

const sampleCharacterSlice = createSlice({
  name: 'sampleCharacter',
  initialState,
  reducers: {
    selectSampleCharacter: (state, action) => {
      // Now we store just the character ID or the full object
      const character = action.payload;
      if (character) {
        state.selectedCharacterId = character.uuid || character;
        // If we receive the full object, update it in the array
        if (character.uuid) {
          const index = state.sampleCharacters.findIndex(c => c.uuid === character.uuid);
          if (index !== -1) {
            state.sampleCharacters[index] = character;
          }
        }
      } else {
        state.selectedCharacterId = null;
      }
      state.isCreatingNew = false;
    },

    clearSelectedCharacter: (state) => {
      state.selectedCharacterId = null;
      state.isCreatingNew = false;
    },

    setCreatingNew: (state, action) => {
      state.isCreatingNew = action.payload;
      if (action.payload) {
        state.selectedCharacterId = null;
      }
    },

    clearErrors: (state) => {
      state.errors = {
        portfolio: null,
        characters: null,
        create: null,
        update: null,
        delete: null,
        character: null
      };
    },

    resetSampleCharacterState: () => initialState,

    // Update tracking actions
    trackPendingUpdate: (state, action) => {
      const { characterId, updates } = action.payload;
      if (!state.updateTracking.pendingUpdates[characterId]) {
        state.updateTracking.pendingUpdates[characterId] = {};
      }
      Object.assign(state.updateTracking.pendingUpdates[characterId], updates);
      state.updateTracking.updateStatus[characterId] = 'pending';
      console.log('[UPDATE TRACKING] Pending update for character:', characterId, updates);
    },

    applyOptimisticUpdate: (state, action) => {
      const { characterId, updates } = action.payload;

      // Apply optimistic update to the character in the array (single source of truth)
      const characterIndex = state.sampleCharacters.findIndex(char => char.uuid === characterId);
      if (characterIndex !== -1) {
        state.sampleCharacters[characterIndex] = {
          ...state.sampleCharacters[characterIndex],
          ...updates
        };
      }

      // Track the optimistic update
      state.updateTracking.optimisticUpdates[characterId] = updates;
      state.updateTracking.updateStatus[characterId] = 'processing';
      console.log('[UPDATE TRACKING] Applied optimistic update for character:', characterId);
    },

    confirmUpdate: (state, action) => {
      const { characterId, serverData } = action.payload;

      // Clear pending and optimistic updates
      delete state.updateTracking.pendingUpdates[characterId];
      delete state.updateTracking.optimisticUpdates[characterId];

      // Update timestamp
      state.updateTracking.lastUpdateTimestamps[characterId] = Date.now();
      state.updateTracking.updateStatus[characterId] = 'completed';

      // Apply server data to the array (single source of truth)
      const characterIndex = state.sampleCharacters.findIndex(char => char.uuid === characterId);
      if (characterIndex !== -1) {
        state.sampleCharacters[characterIndex] = serverData;
      }

      console.log('[UPDATE TRACKING] Confirmed update for character:', characterId);
    },

    revertOptimisticUpdate: (state, action) => {
      const { characterId, originalData } = action.payload;

      // Revert to original data in the array
      const characterIndex = state.sampleCharacters.findIndex(char => char.uuid === characterId);
      if (characterIndex !== -1) {
        state.sampleCharacters[characterIndex] = originalData;
      }

      // Clear optimistic update tracking
      delete state.updateTracking.optimisticUpdates[characterId];
      state.updateTracking.updateStatus[characterId] = 'failed';

      console.log('[UPDATE TRACKING] Reverted optimistic update for character:', characterId);
    },

    queueUpdate: (state, action) => {
      const { characterId, operation, data } = action.payload;
      state.updateTracking.updateQueue.push({
        characterId,
        operation,
        data,
        timestamp: Date.now()
      });
      console.log('[UPDATE TRACKING] Queued update for character:', characterId, operation);
    },

    processUpdateQueue: (state) => {
      // Process next item in queue
      if (state.updateTracking.updateQueue.length > 0) {
        const nextUpdate = state.updateTracking.updateQueue.shift();
        console.log('[UPDATE TRACKING] Processing queued update:', nextUpdate);
        // The actual processing will be handled by middleware/thunk
        return nextUpdate;
      }
    },

    clearUpdateTracking: (state, action) => {
      const characterId = action.payload;
      if (characterId) {
        delete state.updateTracking.pendingUpdates[characterId];
        delete state.updateTracking.optimisticUpdates[characterId];
        delete state.updateTracking.updateStatus[characterId];
        delete state.updateTracking.lastUpdateTimestamps[characterId];
      } else {
        // Clear all tracking
        state.updateTracking = initialState.updateTracking;
      }
    },

    // Form session actions
    initializeFormSession: (state, action) => {
      const character = action.payload;

      // Store original values for change tracking
      state.formSession.original = character ? {
        name: character.name,
        description: character.description,
        role: character.role,
        age: character.age,
        gender: character.gender,
        characterType: character.characterType,
        genre: character.genre,
        tags: character.tags || [],
        scriptContent: character.defaultSpeechSample?.textContent || '',
        avatarUrl: character.avatarUrl,
        voiceUrl: character.defaultSpeechSample?.voiceS3Url,
        fileDuration: character.defaultSpeechSample?.fileDuration
      } : null;

      // Initialize current values (copy of original)
      state.formSession.current = state.formSession.original ?
        { ...state.formSession.original } : null;

      // Reset session state
      state.formSession.audio = {
        isRecording: false,
        recordingDuration: 0,
        audioBlob: null,
        audioUrl: null,
        audioSource: character?.defaultSpeechSample?.voiceS3Url ? 'existing' : null,
        uploadedFileName: character?.defaultSpeechSample?.voiceFileName || '',
        uploadedFileSize: 0,
        isReplacing: false,
        error: null
      };

      state.formSession.avatar = {
        file: null,
        previewUrl: null,
        hasChanges: false,
        uploadStatus: 'idle',
        uploadedUrl: null,
        error: null
      };

      state.formSession.changes = {
        hasCharacterChanges: false,
        hasAudioChange: false,
        hasTextChange: false,
        hasAvatarChange: false,
        isDirty: false,
        changedFields: []
      };

      console.log('[FORM SESSION] Initialized for character:', character?.uuid || 'new');
    },

    updateFormField: (state, action) => {
      const { field, value } = action.payload;

      if (state.formSession.current) {
        state.formSession.current[field] = value;

        // Check if different from original
        const hasChanged = state.formSession.original?.[field] !== value;

        // Update change tracking
        const changedFields = state.formSession.changes.changedFields;
        if (hasChanged && !changedFields.includes(field)) {
          changedFields.push(field);
        } else if (!hasChanged) {
          const index = changedFields.indexOf(field);
          if (index > -1) changedFields.splice(index, 1);
        }

        // Special handling for scriptContent
        if (field === 'scriptContent') {
          state.formSession.changes.hasTextChange = hasChanged;
        }

        state.formSession.changes.hasCharacterChanges = changedFields.length > 0;
        state.formSession.changes.isDirty =
          state.formSession.changes.hasCharacterChanges ||
          state.formSession.changes.hasAudioChange ||
          state.formSession.changes.hasTextChange ||
          state.formSession.changes.hasAvatarChange;
      }
    },

    // Audio actions
    startRecordingAction: (state) => {
      state.formSession.audio.isRecording = true;
      state.formSession.audio.recordingDuration = 0;
      state.formSession.audio.error = null;
    },

    stopRecordingAction: (state) => {
      state.formSession.audio.isRecording = false;
    },

    setRecordedAudio: (state, action) => {
      const { blob, duration } = action.payload;

      // Clean up old URL if exists
      if (state.formSession.audio.audioUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(state.formSession.audio.audioUrl);
      }

      const url = URL.createObjectURL(blob);

      state.formSession.audio.audioBlob = blob;
      state.formSession.audio.audioUrl = url;
      state.formSession.audio.recordingDuration = duration;
      state.formSession.audio.audioSource = 'recording';
      state.formSession.audio.isReplacing = false;

      // Update change tracking
      state.formSession.changes.hasAudioChange = true;
      state.formSession.changes.isDirty = true;
    },

    setUploadedAudio: (state, action) => {
      const { blob, url, fileName, fileSize, duration } = action.payload;

      // Clean up old URL if exists
      if (state.formSession.audio.audioUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(state.formSession.audio.audioUrl);
      }

      state.formSession.audio.audioBlob = blob;
      state.formSession.audio.audioUrl = url;
      state.formSession.audio.uploadedFileName = fileName;
      state.formSession.audio.uploadedFileSize = fileSize;
      state.formSession.audio.recordingDuration = duration;
      state.formSession.audio.audioSource = 'upload';
      state.formSession.audio.isReplacing = false;

      // Update change tracking
      state.formSession.changes.hasAudioChange = true;
      state.formSession.changes.isDirty = true;
    },

    updateRecordingDuration: (state) => {
      if (state.formSession.audio.isRecording) {
        state.formSession.audio.recordingDuration += 1;
      }
    },

    replaceAudioAction: (state) => {
      // Clean up current audio
      if (state.formSession.audio.audioUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(state.formSession.audio.audioUrl);
      }

      state.formSession.audio.isReplacing = true;
      state.formSession.audio.audioBlob = null;
      state.formSession.audio.audioUrl = null;
      state.formSession.audio.audioSource = null;
      state.formSession.audio.recordingDuration = 0;
      state.formSession.audio.uploadedFileName = '';
      state.formSession.audio.uploadedFileSize = 0;
    },

    cancelReplaceAudioAction: (state) => {
      state.formSession.audio.isReplacing = false;
      state.formSession.audio.audioSource = 'existing';

      // Restore existing audio display info
      if (state.formSession.original?.voiceUrl) {
        state.formSession.audio.recordingDuration = state.formSession.original.fileDuration || 0;
      }

      // Reset audio change flag if no new audio was recorded
      if (!state.formSession.audio.audioBlob) {
        state.formSession.changes.hasAudioChange = false;
        // Recalculate isDirty
        state.formSession.changes.isDirty =
          state.formSession.changes.hasCharacterChanges ||
          state.formSession.changes.hasTextChange ||
          state.formSession.changes.hasAvatarChange;
      }
    },

    clearAudio: (state) => {
      if (state.formSession.audio.audioUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(state.formSession.audio.audioUrl);
      }

      state.formSession.audio = {
        isRecording: false,
        recordingDuration: 0,
        audioBlob: null,
        audioUrl: null,
        audioSource: null,
        uploadedFileName: '',
        uploadedFileSize: 0,
        isReplacing: false,
        error: null
      };

      state.formSession.changes.hasAudioChange = false;
      state.formSession.changes.isDirty =
        state.formSession.changes.hasCharacterChanges ||
        state.formSession.changes.hasTextChange ||
        state.formSession.changes.hasAvatarChange;
    },

    setAudioError: (state, action) => {
      state.formSession.audio.error = action.payload;
    },

    // Avatar actions
    setAvatarFile: (state, action) => {
      const { file, url } = action.payload;

      // Clean up old URL if exists
      if (state.formSession.avatar.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(state.formSession.avatar.previewUrl);
      }

      state.formSession.avatar.file = file;
      state.formSession.avatar.previewUrl = url;
      state.formSession.avatar.hasChanges = true;
      state.formSession.avatar.error = null;

      // Update change tracking
      state.formSession.changes.hasAvatarChange = true;
      state.formSession.changes.isDirty = true;
    },

    setAvatarUploading: (state, action) => {
      state.formSession.avatar.uploadStatus = action.payload ? 'uploading' : 'idle';
    },

    setAvatarUploaded: (state, action) => {
      state.formSession.avatar.uploadStatus = 'success';
      state.formSession.avatar.uploadedUrl = action.payload;
      state.formSession.avatar.hasChanges = false;
    },

    setAvatarError: (state, action) => {
      state.formSession.avatar.uploadStatus = 'error';
      state.formSession.avatar.error = action.payload;
    },

    clearAvatar: (state) => {
      if (state.formSession.avatar.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(state.formSession.avatar.previewUrl);
      }

      state.formSession.avatar = {
        file: null,
        previewUrl: null,
        hasChanges: false,
        uploadStatus: 'idle',
        uploadedUrl: null,
        error: null
      };

      state.formSession.changes.hasAvatarChange = false;
      state.formSession.changes.isDirty =
        state.formSession.changes.hasCharacterChanges ||
        state.formSession.changes.hasAudioChange ||
        state.formSession.changes.hasTextChange;
    },

    // Form metadata actions
    setFormDirty: (state, action) => {
      state.formSession.changes.isDirty = action.payload;
    },

    setSaveError: (state, action) => {
      state.formSession.saveError = action.payload;
    },

    setLastSaved: (state) => {
      state.formSession.lastSaved = new Date().toISOString();
    },

    clearFormSession: (state) => {
      // Clean up blob URLs
      if (state.formSession.audio.audioUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(state.formSession.audio.audioUrl);
      }
      if (state.formSession.avatar.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(state.formSession.avatar.previewUrl);
      }

      // Reset to initial state
      state.formSession = initialState.formSession;
    }
  },

  extraReducers: (builder) => {
    // Fetch primary portfolio
    builder
      .addCase(fetchPrimaryPortfolio.pending, (state) => {
        state.loading.portfolio = true;
        state.errors.portfolio = null;
      })
      .addCase(fetchPrimaryPortfolio.fulfilled, (state, action) => {
        state.loading.portfolio = false;
        state.primaryPortfolio = action.payload;
      })
      .addCase(fetchPrimaryPortfolio.rejected, (state, action) => {
        state.loading.portfolio = false;
        state.errors.portfolio = action.error.message;
      });

    // Fetch sample characters
    builder
      .addCase(fetchSampleCharacters.pending, (state) => {
        state.loading.characters = true;
        state.errors.characters = null;
      })
      .addCase(fetchSampleCharacters.fulfilled, (state, action) => {
        state.loading.characters = false;
        state.sampleCharacters = action.payload;
      })
      .addCase(fetchSampleCharacters.rejected, (state, action) => {
        state.loading.characters = false;
        state.errors.characters = action.error.message;
      });

    // Create sample character
    builder
      .addCase(createSampleCharacter.pending, (state) => {
        state.loading.create = true;
        state.errors.create = null;
      })
      .addCase(createSampleCharacter.fulfilled, (state, action) => {
        state.loading.create = false;
        state.sampleCharacters.push(action.payload);
        state.selectedCharacterId = action.payload.uuid;
        state.isCreatingNew = false;
      })
      .addCase(createSampleCharacter.rejected, (state, action) => {
        state.loading.create = false;
        state.errors.create = action.error.message;
      });

    // Update sample character
    builder
      .addCase(updateSampleCharacter.pending, (state) => {
        state.loading.update = true;
        state.errors.update = null;
      })
      .addCase(updateSampleCharacter.fulfilled, (state, action) => {
        state.loading.update = false;
        const index = state.sampleCharacters.findIndex(
          char => char.uuid === action.payload.uuid
        );
        if (index !== -1) {
          state.sampleCharacters[index] = action.payload;
        }
        // No need to update selectedSampleCharacter - the form will get it from the array
      })
      .addCase(updateSampleCharacter.rejected, (state, action) => {
        state.loading.update = false;
        state.errors.update = action.error.message;
      });

    // Delete sample character
    builder
      .addCase(deleteSampleCharacter.pending, (state) => {
        state.loading.delete = true;
        state.errors.delete = null;
      })
      .addCase(deleteSampleCharacter.fulfilled, (state, action) => {
        state.loading.delete = false;
        state.sampleCharacters = state.sampleCharacters.filter(
          char => char.uuid !== action.payload
        );
        if (state.selectedCharacterId === action.payload) {
          state.selectedCharacterId = null;
        }
      })
      .addCase(deleteSampleCharacter.rejected, (state, action) => {
        state.loading.delete = false;
        state.errors.delete = action.error.message;
      });

    // Get single sample character
    builder
      .addCase(getSampleCharacter.pending, (state) => {
        state.loading.character = true;
        state.errors.character = null;
      })
      .addCase(getSampleCharacter.fulfilled, (state, action) => {
        state.loading.character = false;
        // Update the character in the array (single source of truth)
        const index = state.sampleCharacters.findIndex(
          char => char.uuid === action.payload.uuid
        );
        if (index !== -1) {
          state.sampleCharacters[index] = action.payload;
        } else {
          // If character not in array, add it
          state.sampleCharacters.push(action.payload);
        }
        // Select the character by ID
        state.selectedCharacterId = action.payload.uuid;
      })
      .addCase(getSampleCharacter.rejected, (state, action) => {
        state.loading.character = false;
        state.errors.character = action.error.message;
      });

    // Create sample character complete (atomic transaction)
    builder
      .addCase(createSampleCharacterComplete.pending, (state) => {
        state.loading.create = true;
        state.errors.create = null;
      })
      .addCase(createSampleCharacterComplete.fulfilled, (state, action) => {
        state.loading.create = false;
        // Add the complete character with all speech sample data
        state.sampleCharacters.push(action.payload);
        state.selectedCharacterId = action.payload.uuid;
        state.isCreatingNew = false;
        console.log('[ATOMIC CREATE] Character created with speech sample data');
      })
      .addCase(createSampleCharacterComplete.rejected, (state, action) => {
        state.loading.create = false;
        state.errors.create = action.error.message;
      });

    // Update sample character complete (atomic transaction)
    builder
      .addCase(updateSampleCharacterComplete.pending, (state) => {
        state.loading.update = true;
        state.errors.update = null;
      })
      .addCase(updateSampleCharacterComplete.fulfilled, (state, action) => {
        state.loading.update = false;
        const updatedCharacter = action.payload;

        console.log('[REDUX UPDATE] Received character from server:', updatedCharacter);
        console.log('[REDUX UPDATE] Sample data:', updatedCharacter.sample);

        const index = state.sampleCharacters.findIndex(
          char => char.uuid === updatedCharacter.uuid
        );

        if (index !== -1) {
          // Replace the entire character object with the updated one from server
          state.sampleCharacters[index] = updatedCharacter;

          console.log('[REDUX UPDATE] Updated character at index', index);
          console.log('[REDUX UPDATE] New text content:', updatedCharacter.sample?.textContent);
        } else {
          console.error('[REDUX UPDATE] Character not found in array!', updatedCharacter.uuid);
        }
      })
      .addCase(updateSampleCharacterComplete.rejected, (state, action) => {
        state.loading.update = false;
        state.errors.update = action.error.message;
      });

    // Create variation for sample
    builder
      .addCase(createVariationForSample.fulfilled, (state, action) => {
        // Update the character in the array (single source of truth)
        const character = state.sampleCharacters.find(
          char => char.uuid === action.payload.characterId
        );
        if (character) {
          if (!character.variations) {
            character.variations = [];
          }
          character.variations.push(action.payload.variation);
          character.variationCount = (character.variationCount || 0) + 1;
          character.hasAudio = true;
        }
        // No need to update separately - form will get it from the array
      });
  }
});

// Export actions
export const {
  selectSampleCharacter,
  clearSelectedCharacter,
  setCreatingNew,
  clearErrors,
  resetSampleCharacterState,
  // Update tracking actions
  trackPendingUpdate,
  applyOptimisticUpdate,
  confirmUpdate,
  revertOptimisticUpdate,
  queueUpdate,
  processUpdateQueue,
  clearUpdateTracking,
  // Form session actions
  initializeFormSession,
  updateFormField,
  // Audio actions
  startRecordingAction,
  stopRecordingAction,
  setRecordedAudio,
  setUploadedAudio,
  updateRecordingDuration,
  replaceAudioAction,
  cancelReplaceAudioAction,
  clearAudio,
  setAudioError,
  // Avatar actions
  setAvatarFile,
  setAvatarUploading,
  setAvatarUploaded,
  setAvatarError,
  clearAvatar,
  // Form metadata actions
  setFormDirty,
  setSaveError,
  setLastSaved,
  clearFormSession
} = sampleCharacterSlice.actions;

// Selectors
export const selectPrimaryPortfolio = (state) => state.sampleCharacter.primaryPortfolio;
export const selectSampleCharacters = (state) => state.sampleCharacter.sampleCharacters;
export const selectSelectedCharacterId = (state) => state.sampleCharacter.selectedCharacterId;

// Derived selector - gets the selected character from the array (single source of truth)
export const selectSelectedSampleCharacter = (state) => {
  const selectedId = state.sampleCharacter.selectedCharacterId;
  if (!selectedId) return null;

  // Always get the character from the array, ensuring single source of truth
  const character = state.sampleCharacter.sampleCharacters.find(
    char => char.uuid === selectedId
  );

  console.log('[SELECTOR] Selected character:', selectedId, 'Found:', !!character,
    'Text content:', character?.sample?.textContent?.substring(0, 50));

  // Return the character object - Redux will detect if this is a different object reference
  return character || null;
};

export const selectIsCreatingNew = (state) => state.sampleCharacter.isCreatingNew;
export const selectLoadingStates = (state) => state.sampleCharacter.loading;
export const selectErrors = (state) => state.sampleCharacter.errors;

// Update tracking selectors
export const selectUpdateTracking = (state) => state.sampleCharacter.updateTracking;
export const selectPendingUpdates = (state) => state.sampleCharacter.updateTracking.pendingUpdates;
export const selectUpdateStatus = (characterId) => (state) =>
  state.sampleCharacter.updateTracking.updateStatus[characterId];
export const selectCharacterUpdateTimestamp = (characterId) => (state) =>
  state.sampleCharacter.updateTracking.lastUpdateTimestamps[characterId];
export const selectOptimisticUpdates = (characterId) => (state) =>
  state.sampleCharacter.updateTracking.optimisticUpdates[characterId];

// Form session selectors
export const selectFormSession = (state) => state.sampleCharacter.formSession;
export const selectFormSessionOriginal = (state) => state.sampleCharacter.formSession.original;
export const selectFormSessionCurrent = (state) => state.sampleCharacter.formSession.current;
export const selectFormSessionAudio = (state) => state.sampleCharacter.formSession.audio;
export const selectFormSessionAvatar = (state) => state.sampleCharacter.formSession.avatar;
export const selectFormSessionChanges = (state) => state.sampleCharacter.formSession.changes;
export const selectFormSessionIsDirty = (state) => state.sampleCharacter.formSession.changes.isDirty;
export const selectFormSessionLastSaved = (state) => state.sampleCharacter.formSession.lastSaved;
export const selectFormSessionSaveError = (state) => state.sampleCharacter.formSession.saveError;

// Audio state selectors
export const selectAudioIsRecording = (state) => state.sampleCharacter.formSession.audio.isRecording;
export const selectAudioBlob = (state) => state.sampleCharacter.formSession.audio.audioBlob;
export const selectAudioUrl = (state) => state.sampleCharacter.formSession.audio.audioUrl;
export const selectAudioSource = (state) => state.sampleCharacter.formSession.audio.audioSource;
export const selectAudioIsReplacing = (state) => state.sampleCharacter.formSession.audio.isReplacing;
export const selectAudioDuration = (state) => state.sampleCharacter.formSession.audio.recordingDuration;
export const selectAudioError = (state) => state.sampleCharacter.formSession.audio.error;

// Avatar state selectors
export const selectAvatarFile = (state) => state.sampleCharacter.formSession.avatar.file;
export const selectAvatarPreviewUrl = (state) => state.sampleCharacter.formSession.avatar.previewUrl;
export const selectAvatarHasChanges = (state) => state.sampleCharacter.formSession.avatar.hasChanges;
export const selectAvatarUploadStatus = (state) => state.sampleCharacter.formSession.avatar.uploadStatus;
export const selectAvatarError = (state) => state.sampleCharacter.formSession.avatar.error;

// Export reducer
export default sampleCharacterSlice.reducer;
